// The Library's Free webinars, at laptop and phone width (Adam, 9 Oct, in place of the boards' next 2
// and the latest replay): "Anything that's live today or a new one to register for, that should be
// separated. But all the recording should be shown as like a library for people to re-watch."
// - Coming up, called out on its own at the top, only when there's at least 1: every webinar shown that
//   hasn't ended, soonest first, each opening its card on Free webinars, where Register on Zoom is.
//   With no thumbnail anywhere in the section, its cards keep the boards' compact look.
// - Watch again: every replay that's up, newest first, as thumbnail cards, each opening its own page,
//   where it plays (Adam, 23 Oct), in 1 row that scrolls sideways inside itself (Adam, 11 Oct: the
//   replays keep coming, so the row keeps the Library's height the same however many there are, and
//   Free resources doesn't sink).
//   On a phone (760px and under) it's a rail to swipe, each card 80% of the width so the next one
//   always shows at the right edge (Adam's iPhone walkthrough of 9 Oct: nothing hidden off the side
//   without a cue); wider, 3 whole cards at a time (2 under 1180px), with arrow buttons beside the
//   sub-label once there are more than fit. The arrows are aria-disabled at either end, rather than
//   disabled, so a keyboard's focus stays on them. See all, beside the sub-label, opens Free webinars at
//   its own Watch again, for every replay at once; it's there while Coming up sits between Watch again
//   and the section's own link to Free webinars, which otherwise sits right above and does the same.
// A card's thumbnail is Adam's own design, or the date panel (free/Thumbnail.jsx, free/thumbnails.js).
// Staff viewing as a member see the member's thumbnails too (Adam, 10 Oct: View as shows what the
// member sees): sign-file reads them as the member.
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'
import { Link } from '../lib/router.jsx'
import { Arrow, SideChevron } from '../shell/Marks.jsx'
import { useThumbnails } from '../free/thumbnails.js'
import { ThumbnailFrame } from '../free/Thumbnail.jsx'
import { DateBlock } from './DateBlock.jsx'

// A card with its 16:9 thumbnail: the kind (Replay, or Live on Zoom) with its dot, the date, the
// title and what pressing it does
function ThumbCard({ w, thumbs }) {
  return (
    <Link to={w.href} className="wthumb">
      <ThumbnailFrame thumb={thumbs.frame(w)} onError={e => thumbs.failed(w, e.currentTarget.src)}
        onLoad={e => thumbs.loaded(w, e.currentTarget.src)}>
        <DateBlock block={w.block} />
      </ThumbnailFrame>
      <span className="wthumb-body">
        <span className="wthumb-kind"><span className={`dot dot--${w.accent}`} />{w.kind}</span>
        <span className="wthumb-date">{w.date}</span>
        <span className="wthumb-title">{w.title}</span>
        <span className="wthumb-action">{w.action}<Arrow /></span>
      </span>
    </Link>
  )
}

// The boards' compact card for 1 coming up, at laptop width
function CompactCard({ w }) {
  return (
    <Link to={w.href} className="wcard">
      <DateBlock block={w.block} />
      <div className="session-text">
        <div className="session-product"><span className={`dot dot--${w.accent}`} />{w.kind}</div>
        <div className="session-title">{w.title}</div>
        <div className="webinar-action">{w.action}<Arrow /></div>
      </div>
    </Link>
  )
}

// The boards' compact row for 1 coming up, at phone width
function CompactRow({ w }) {
  return (
    <Link to={w.href} className="ph-session ph-session--link">
      <DateBlock block={w.block} small />
      <span className="ph-session-text">
        <span className="ph-session-product"><span className={`dot dot--small dot--${w.accent}`} />{w.kind}</span>
        <span className="ph-session-title">{w.title}</span>
        <span className="ph-action">{w.action}</span>
      </span>
    </Link>
  )
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Where the row can go from where it is: back (towards the newest), on (towards the oldest), and
// whether it scrolls at all. 1px of slack for the fractions a browser leaves.
function rowEnds(el) {
  const room = el.scrollWidth - el.clientWidth
  return { scrolls: room > 1, back: el.scrollLeft > 1, on: el.scrollLeft < room - 1 }
}

// The distance from 1 card's start to the next, and how many whole cards show at once
function rowStep(el) {
  const card = el.querySelector('.wthumb')
  if (!card) return { per: el.clientWidth, shown: 1 }
  const style = getComputedStyle(el)
  const gap = parseFloat(style.columnGap) || 0
  const per = card.getBoundingClientRect().width + gap
  const inside = el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)
  return { per, shown: Math.max(1, Math.floor((inside + gap + 1) / per)) }
}

// Watch again's row, with its sub-label, See all and (wider than a phone) the arrow buttons
function ReplayRow({ replays, thumbs, phone, seeAll, id }) {
  const rail = useRef(null)
  // where the last press is taking the row, while it gets there, so a 2nd press goes on from there
  const aim = useRef(null)
  const [ends, setEnds] = useState({ scrolls: false, back: false, on: false })
  const measure = useCallback(() => {
    const el = rail.current
    if (!el) return
    const next = rowEnds(el)
    setEnds(was => (was.scrolls === next.scrolls && was.back === next.back && was.on === next.on ? was : next))
  }, [])
  useLayoutEffect(() => {
    const el = rail.current
    if (!el) return
    measure()
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => { frame = 0; measure() })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    watch?.observe(el)
    return () => {
      el.removeEventListener('scroll', onScroll)
      watch?.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [measure, replays.length, phone])

  // a page of whole cards at a time, landing on a card's start
  const move = dir => {
    const el = rail.current
    if (!el || !ends[dir < 0 ? 'back' : 'on']) return
    const { per, shown } = rowStep(el)
    const room = el.scrollWidth - el.clientWidth
    const from = aim.current && performance.now() < aim.current.until ? aim.current.left : el.scrollLeft
    const left = Math.min(room, Math.max(0, Math.round(from / per + dir * shown) * per))
    const smooth = !reducedMotion()
    aim.current = smooth ? { left, until: performance.now() + 700 } : null
    el.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' })
  }

  const railId = `${id}-row`
  const arrows = !phone && ends.scrolls
  return (
    <section className="lib-web-part" aria-labelledby={`${id}-again`}>
      <div className="lib-sub-head">
        <h3 className="lib-sub" id={`${id}-again`}>Watch again</h3>
        {(seeAll || arrows) && (
          <div className="wrow-tools">
            {seeAll && (
              <Link to="/free-webinars#fw-watch-again" className={phone ? 'ph-all wrow-all' : 'lib-all wrow-all'}>
                See all<span className="sr-only"> replays</span>
              </Link>
            )}
            {arrows && (
              <>
                <button type="button" className="wrow-btn" aria-label="Show newer replays" aria-controls={railId}
                  aria-disabled={!ends.back} onClick={() => move(-1)}><SideChevron dir="back" /></button>
                <button type="button" className="wrow-btn" aria-label="Show older replays" aria-controls={railId}
                  aria-disabled={!ends.on} onClick={() => move(1)}><SideChevron dir="on" /></button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="wrail" id={railId} ref={rail}>{replays.map(w => <ThumbCard key={w.id} w={w} thumbs={thumbs} />)}</div>
    </section>
  )
}

export function LibraryWebinars({ webinars, phone }) {
  const { upcoming, replays, thumbnails } = webinars
  const thumbs = useThumbnails([...upcoming, ...replays])
  const id = useId()
  if (!upcoming.length && !replays.length) return null
  return (
    <section className={phone ? 'ph-section lib-web' : 'lib-section lib-web'} aria-labelledby={`${id}-h`}>
      <div className={phone ? 'ph-section-head' : 'lib-section-head'}>
        <h2 className={phone ? 'ph-h2' : 'lib-h2'} id={`${id}-h`}>Free webinars</h2>
        {phone
          ? <Link to="/free-webinars" className="ph-all">See all</Link>
          : <Link to="/free-webinars" className="lib-all">All free webinars</Link>}
      </div>
      {upcoming.length > 0 && (
        <section className="lib-web-part" aria-labelledby={`${id}-up`}>
          <h3 className="lib-sub" id={`${id}-up`}>Coming up</h3>
          {thumbnails ? (
            <div className="wgrid">{upcoming.map(w => <ThumbCard key={w.id} w={w} thumbs={thumbs} />)}</div>
          ) : phone ? (
            upcoming.map(w => <CompactRow key={w.id} w={w} />)
          ) : (
            <div className="grid grid--3">{upcoming.map(w => <CompactCard key={w.id} w={w} />)}</div>
          )}
        </section>
      )}
      {replays.length > 0 && (
        <ReplayRow replays={replays} thumbs={thumbs} phone={phone} seeAll={upcoming.length > 0} id={id} />
      )}
    </section>
  )
}
